// Location: src/components/admin/schedule/ScheduleRecentComments.tsx
// Purpose: Display recent comments for comment publishing schedules
// Scope: Collapsible list of recent comments with animation

import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Card } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { useNavigate } from 'react-router-dom';
import { Schedule } from '@/types/schedules';

interface CommentUrl {
	url: string;
	username: string;
	createdAt: string;
}

interface ScheduleRecentCommentsProps {
	schedule: Schedule;
	commentUrls: CommentUrl[] | undefined;
}

export const ScheduleRecentComments = React.memo(
	function ScheduleRecentComments({
		schedule,
		commentUrls,
	}: ScheduleRecentCommentsProps) {
		const navigate = useNavigate();
		const [showUrls, setShowUrls] = useState(false);

		if (
			(schedule.type !== 'comments' &&
				schedule.type !== 'profile_comment') ||
			!commentUrls ||
			commentUrls.length === 0
		) {
			return null;
		}

		return (
			<div className='mt-4'>
				<div className='flex items-center justify-between mb-4'>
					<div className='flex items-center gap-2'>
						<motion.button
							onClick={() => setShowUrls(!showUrls)}
							className='w-8 h-8 rounded-full bg-purple-900 flex items-center justify-center text-white hover:bg-purple-700 transition-colors'
							animate={{ rotate: showUrls ? 180 : 0 }}
							transition={{ duration: 0.2 }}
						>
							{showUrls ? '🔽' : '▶️'}
						</motion.button>
						<h3 className='text-xl font-bold text-pink-500'>
							Last 5 Published Comments
						</h3>
					</div>
					{showUrls && (
						<Button
							variant='outline'
							size='sm'
							onClick={() => navigate('/admin/content')}
							className='bg-gradient-to-r from-purple-500 to-pink-500 hover:from-purple-600 hover:to-pink-600 text-white border-none'
						>
							📚 View All
						</Button>
					)}
				</div>

				<AnimatePresence>
					{showUrls && (
						<motion.div
							initial={{ opacity: 0, height: 0 }}
							animate={{ opacity: 1, height: 'auto' }}
							exit={{ opacity: 0, height: 0 }}
							transition={{ duration: 0.2 }}
							className='space-y-2'
						>
							{commentUrls.map((url: CommentUrl, idx: number) => (
								<motion.div
									key={url.url}
									initial={{ opacity: 0, y: -10 }}
									animate={{
										opacity: 1,
										y: 0,
										transition: {
											delay: idx * 0.05,
										},
									}}
								>
									<Card className='p-4'>
										<div className='flex justify-between items-center'>
											<div className='space-y-1'>
												<div className='flex items-center gap-2'>
													<h4 className='font-semibold'>
														@{url.username}
													</h4>
													<span className='px-2 py-1 rounded-full text-xs font-semibold bg-purple-100 text-purple-800'>
														COMMENT
													</span>
												</div>
												<a
													href={url.url}
													target='_blank'
													rel='noopener noreferrer'
													className='text-blue-500 hover:text-blue-700 underline text-sm'
												>
													{url.url}
												</a>
												<p className='text-xs text-gray-400'>
													Published:{' '}
													{new Date(
														url.createdAt
													).toLocaleString()}
												</p>
											</div>
										</div>
									</Card>
								</motion.div>
							))}
						</motion.div>
					)}
				</AnimatePresence>
			</div>
		);
	}
);
